import { format } from "date-fns";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";

import { MEDDREAM_LABELS } from "../labels";
import type { IMeddreamHourlyMetric } from "../schemas/meddream-hourly";

const chartConfig = {
  uniqueSessionsCount: {
    label: MEDDREAM_LABELS.sessionsInHour.label,
    color: "var(--chart-3)",
  },
} satisfies ChartConfig;

export interface SessionsActivityChartProps {
  metrics: IMeddreamHourlyMetric[];
}

/**
 * Barras compactas de actividad por hora (uniqueSessionsCount).
 * No es “usuarios únicos del período”.
 */
export function SessionsActivityChart({
  metrics,
}: SessionsActivityChartProps): React.JSX.Element {
  const data = metrics.map((m) => ({
    id: m.id,
    localLabel: format(m.hourStart, "dd/MM HH:mm"),
    uniqueSessionsCount: m.uniqueSessionsCount,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{MEDDREAM_LABELS.sessionActivity.label}</CardTitle>
        <CardDescription>{MEDDREAM_LABELS.sessionsInHour.description}</CardDescription>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="flex h-[180px] items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-900/40">
            <p className="text-sm text-muted-foreground">Sin datos</p>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="aspect-auto h-[200px] w-full">
            <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
              <XAxis
                dataKey="localLabel"
                tickLine={false}
                axisLine={false}
                minTickGap={32}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={32}
                allowDecimals={false}
                tick={{ fontSize: 11 }}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar
                dataKey="uniqueSessionsCount"
                fill="var(--color-uniqueSessionsCount)"
                radius={[4, 4, 0, 0]}
                name={MEDDREAM_LABELS.sessionsInHour.label}
                isAnimationActive={false}
              />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
